/**
 * Secure API Key Manager
 * Handles encryption, storage, and retrieval of API keys
 */

import { EncryptionService } from './encryption-service';

export interface APIKeyManager {
  store(provider: string, key: string): Promise<void>;
  retrieve(provider: string): Promise<string | null>;
  remove(provider: string): Promise<void>;
  validate(provider: string, key: string): Promise<boolean>;
  rotate(provider: string): Promise<string>;
  listProviders(): Promise<string[]>;
  hasKey(provider: string): Promise<boolean>;
}

export interface SecureStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  list(): Promise<string[]>;
}

/**
 * Browser-based secure storage using localStorage with encryption
 */
class BrowserSecureStorage implements SecureStorage {
  private prefix = 'vibecode-secure-';

  async get(key: string): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.prefix + key);
  }

  async set(key: string, value: string): Promise<void> {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.prefix + key, value);
  }

  async remove(key: string): Promise<void> {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.prefix + key);
  }

  async list(): Promise<string[]> {
    if (typeof window === 'undefined') return [];
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.prefix)) {
        keys.push(key.substring(this.prefix.length));
      }
    }
    return keys;
  }
}

export class SecureAPIKeyManager implements APIKeyManager {
  private encryption: EncryptionService;
  private storage: SecureStorage;
  private keyPrefix = 'api-key-';
  private validationCache = new Map<string, { valid: boolean; timestamp: number }>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor(
    encryption?: EncryptionService,
    storage?: SecureStorage
  ) {
    this.encryption = encryption || new EncryptionService();
    this.storage = storage || new BrowserSecureStorage();
  }

  async store(provider: string, key: string): Promise<void> {
    if (!key || !provider) {
      throw new Error('Provider and key are required');
    }

    // Validate the key format
    this.validateKeyFormat(provider, key);

    let encrypted: string;
    try {
      // Try to use proper encryption if available
      if (typeof window !== 'undefined' && this.encryption.isAvailable()) {
        encrypted = await this.encryption.encrypt(key);
      } else {
        // Fallback to simple base64 for server/non-secure environments
        encrypted = Buffer.from(key).toString('base64');
      }
    } catch (error) {
      console.warn('Encryption failed, using base64:', error);
      encrypted = Buffer.from(key).toString('base64');
    }
    
    // Store the encrypted key
    await this.storage.set(this.keyPrefix + provider, encrypted);
    
    // Clear validation cache for this provider
    this.validationCache.delete(provider);
  }

  async retrieve(provider: string): Promise<string | null> {
    console.log(`[APIKeyManager] Retrieving key for ${provider}`);
    const encrypted = await this.storage.get(this.keyPrefix + provider);
    if (!encrypted) {
      console.log(`[APIKeyManager] No encrypted key found for ${provider}`);
      return null;
    }

    console.log(`[APIKeyManager] Found encrypted key for ${provider}, attempting to decrypt...`);
    try {
      // For server-side, we need a simpler approach
      // In production, use proper server-side encryption
      if (typeof window === 'undefined') {
        // Server-side: Just base64 decode for now
        const decoded = Buffer.from(encrypted, 'base64').toString('utf-8');
        console.log(`[APIKeyManager] Successfully decoded key for ${provider} (server-side)`);
        return decoded;
      }
      const decrypted = await this.encryption.decrypt(encrypted);
      console.log(`[APIKeyManager] Successfully decrypted key for ${provider} (client-side)`);
      return decrypted;
    } catch (error) {
      console.error('Failed to decrypt API key:', error);
      // Try simple base64 decode as fallback
      try {
        const decoded = Buffer.from(encrypted, 'base64').toString('utf-8');
        console.log(`[APIKeyManager] Used fallback base64 decode for ${provider}`);
        return decoded;
      } catch {
        console.error(`[APIKeyManager] Failed to decode key for ${provider} even with fallback`);
        return null;
      }
    }
  }

  async remove(provider: string): Promise<void> {
    await this.storage.remove(this.keyPrefix + provider);
    this.validationCache.delete(provider);
  }

  async validate(provider: string, key: string): Promise<boolean> {
    // Check cache first
    const cached = this.validationCache.get(provider);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.valid;
    }

    // Perform validation based on provider
    let valid = false;
    
    try {
      switch (provider) {
        case 'openai':
          valid = await this.validateOpenAIKey(key);
          break;
        case 'anthropic':
          valid = await this.validateAnthropicKey(key);
          break;
        case 'xai':
          valid = await this.validateXAIKey(key);
          break;
        default:
          // For custom providers, just check format
          valid = this.isValidKeyFormat(key);
      }
    } catch (error) {
      console.error('Key validation error:', error);
      valid = false;
    }

    // Cache the result
    this.validationCache.set(provider, { valid, timestamp: Date.now() });
    
    return valid;
  }

  async rotate(provider: string): Promise<string> {
    // In a real implementation, this would call the provider's API to rotate the key
    // For now, we'll just generate a new mock key
    const newKey = this.generateMockKey(provider);
    await this.store(provider, newKey);
    return newKey;
  }

  async listProviders(): Promise<string[]> {
    const keys = await this.storage.list();
    return keys
      .filter(key => key.startsWith(this.keyPrefix))
      .map(key => key.substring(this.keyPrefix.length));
  }

  async hasKey(provider: string): Promise<boolean> {
    const key = await this.retrieve(provider);
    return key !== null;
  }

  // Private validation methods
  private validateKeyFormat(provider: string, key: string): void {
    if (!this.isValidKeyFormat(key)) {
      throw new Error(`Invalid key format for provider: ${provider}`);
    }

    // Provider-specific format validation
    switch (provider) {
      case 'openai':
        if (!key.startsWith('sk-') && !key.startsWith('sk-proj-')) {
          throw new Error('OpenAI keys must start with "sk-" or "sk-proj-"');
        }
        break;
      case 'anthropic':
        if (!key.startsWith('sk-ant-')) {
          throw new Error('Anthropic keys must start with "sk-ant-"');
        }
        break;
    }
  }

  private isValidKeyFormat(key: string): boolean {
    // Basic validation: non-empty, reasonable length, alphanumeric + common chars
    return /^[a-zA-Z0-9\-_]{20,}$/.test(key);
  }

  private async validateOpenAIKey(key: string): Promise<boolean> {
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${key}`,
        },
      });
      return response.status === 200;
    } catch {
      return false;
    }
  }

  private async validateAnthropicKey(key: string): Promise<boolean> {
    // Skip validation on client side due to CORS
    if (typeof window !== 'undefined') {
      console.log('[APIKeyManager] Skipping Anthropic key validation on client side (CORS)');
      // Just do basic format check
      return key.startsWith('sk-ant-') && key.length > 40;
    }
    
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': key,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          messages: [{ role: 'user', content: 'test' }],
          max_tokens: 1,
        }),
      });
      // 401 means invalid key, 400 or 200 means valid key
      return response.status !== 401;
    } catch {
      return false;
    }
  }

  private async validateXAIKey(key: string): Promise<boolean> {
    // XAI validation would go here
    // For now, just validate format
    return this.isValidKeyFormat(key);
  }

  private generateMockKey(provider: string): string {
    const prefix = {
      openai: 'sk-proj-',
      anthropic: 'sk-ant-',
      xai: 'xai-',
    }[provider] || 'key-';

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = prefix;
    for (let i = 0; i < 48; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  }
}